import { lessons } from './lessonsData';

const indent = (text) =>
  text
    .split('\n')
    .map((line) => (line ? `    ${line}` : ''))
    .join('\n');

const program = ({
  includes = ['#include <stdio.h>'],
  helpers = '',
  mainBody = '',
  mainSignature = 'int main()',
  addReturn = true
}) => {
  const sections = [includes.join('\n')];
  const trimmedHelpers = helpers.trim();
  const trimmedBody = mainBody.trim();
  const finalBody = addReturn
    ? [trimmedBody, 'return 0;'].filter(Boolean).join('\n')
    : trimmedBody;

  if (trimmedHelpers) {
    sections.push(trimmedHelpers);
  }

  sections.push(`${mainSignature} {\n${indent(finalBody)}\n}`);
  return sections.join('\n\n');
};

const normalizeSentence = (text) =>
  text
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[。．]$/, '');

const toSentence = (text) => {
  const normalized = normalizeSentence(text);
  return normalized ? `${normalized}。` : '';
};

const extractLeadSentence = (text) => {
  const compact = text.replace(/\s+/g, ' ').trim();
  const match = compact.match(/^.+?[。．]/);
  return normalizeSentence(match ? match[0] : compact);
};

const unique = (items) => [...new Set(items.filter(Boolean))];

const exerciseExpectationOverrides = {
  '3つの入力値の平均': {
    outputExample: '入力例: `3 4 5` / 出力例: `4.00`'
  },
  'うるう年判定': {
    outputExample: '入力例: `2000` / 出力例: `leap`'
  },
  '文字列の逆順表示': {
    outputExample: '例: `algorithm` を逆順にして `mhtirogla` と 1 行で表示します。'
  },
  'printf の戻り値を確認する': {
    outputExample: '実行例: 1 行目に `C language`、2 行目に `written = 11` を表示します。'
  },
  'scanf の戻り値を確認する': {
    outputExample: '入力例: `42` / 出力例: `read = 1` と `value = 42`'
  }
};

const buildExpectedBehavior = ({ title, description, type, format, solution }) => {
  const override = exerciseExpectationOverrides[title];
  if (override?.expectedBehavior) return override.expectedBehavior;

  if (format === 'エラー探し') {
    return '修正後のコードが最後まで動作し、危険な書き方や誤った結果が残っていない状態を目指してください。';
  }

  if (type === 'graphics') {
    return `${toSentence(description)} 実行後にキャンバス上で位置・色・大きさ・更新順まで確認できる状態を目指してください。`;
  }

  if (solution.includes('scanf(')) {
    return `${toSentence(description)} 入力値を変えても同じ規則で処理でき、入力に応じた結果だけが表示される状態を目指してください。`;
  }

  return `${toSentence(description)} 実行すると最終結果が指定順で表示され、途中で止まらない状態を目指してください。`;
};

const buildAnswerFormatNote = ({ title, type, format, solution }) => {
  const override = exerciseExpectationOverrides[title];
  if (override?.answerFormatNote) return override.answerFormatNote;

  if (format === 'エラー探し') {
    return '修正版では、不要な説明文やデバッグ出力を足さず、問題の原因を取り除いたコードとして提出できる形にまとめてください。';
  }

  if (type === 'graphics') {
    return 'この問題は標準出力よりもキャンバス表示で判定します。説明どおりの図形や動きが見え、不要な `printf` を増やさない形が分かりやすいです。';
  }

  if (solution.includes('scanf(')) {
    return '入力が必要な問題では、半角スペースや改行で値を与え、出力は結果だけを必要な行数で表示してください。確認用の文章を余分に付けない方が判定しやすくなります。';
  }

  return '出力がある問題では、課題で求められた結果だけを指定順で表示してください。途中確認用の文や余計なラベルは付けず、改行位置もそろえると確認しやすくなります。';
};

const buildOutputExample = ({ title, type, format, solution }) => {
  const override = exerciseExpectationOverrides[title];
  if (override?.outputExample) return override.outputExample;

  if (format === 'エラー探し') {
    return '例: 修正後にコンパイルが通り、実行結果やメモリ使用が説明どおりになっていれば正解です。';
  }

  if (type === 'graphics') {
    return '例: 実行するとキャンバス上へ図形やアニメーションが現れ、課題文で指定した見た目になっていれば正解です。';
  }

  if (solution.includes('scanf(')) {
    return '例: 指定された値を入力したとき、その入力に対応する結果だけが 1 行または指定行数で表示されれば正解です。';
  }

  return '例: starterCode に入っている固定データをそのまま使って実行したとき、最終結果だけが表示されれば正解です。';
};

const buildExerciseExpectations = (config) => ({
  expectedBehavior: buildExpectedBehavior(config),
  answerFormatNote: buildAnswerFormatNote(config),
  outputExample: buildOutputExample(config)
});

const extractIncludes = (code) => {
  const matches = code.match(/^#include .+$/gm);
  return matches && matches.length > 0 ? [...new Set(matches)] : ['#include <stdio.h>'];
};

const buildLessonStarter = (lesson, todoList = []) => {
  const lessonSummary = extractLeadSentence(lesson.description);
  const starterComments =
    todoList.length > 0
      ? [
          '// starterCode の使い方:',
          '// - 下の TODO を上から順に埋めてください',
          '// - 必要な変数宣言や式や文は、自分で追加して構いません',
          ...todoList.map((todo, index) => `// TODO ${index + 1}: ${normalizeSentence(todo)}`)
        ].join('\n')
      : lesson.type === 'graphics'
        ? '// レッスン内容を参考に、図形が見えるように描画コードを書いてください'
        : `// レッスン「${lesson.title}」を参考に、${lessonSummary}\n// 入力・計算・表示の流れがつながるようにコードを完成させてください`;
  const helperHint =
    todoList.length > 0 && lesson.category === '関数'
      ? '// TODO: 必要な関数プロトタイプや関数定義を、この位置へ追加してください'
      : todoList.length > 0 && lesson.title === 'グローバル変数とローカル変数'
        ? '// TODO: 必要なグローバル変数や補助関数を、この位置へ追加してください'
        : '';

  return program({
    includes: extractIncludes(lesson.code),
    helpers: helperHint,
    mainBody:
      lesson.type === 'graphics' && todoList.length === 0
        ? `// レッスン「${lesson.title}」を参考に、${lessonSummary}\n// 図形や動きの意図が画面で分かるように描画コードを完成させてください`
        : starterComments
  });
};

const extractTaskComments = (starterCode) =>
  unique(
    starterCode
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.startsWith('//'))
      .map((line) => line.replace(/^\/\/+\s*/, ''))
      .map((line) =>
        line
          .replace('ここを実装してください', '未実装部分を完成させてください')
          .replace('書いてください', '完成させてください')
      )
      .map(normalizeSentence)
      .filter(Boolean)
  );

const getCategoryCheckpoint = (category) => {
  const checkpointMap = {
    基礎: '型、演算順序、整数除算など、見た目では分かりにくい基本仕様を確認してください。',
    入出力: '書式指定子、`scanf` のアドレス渡し、`EOF` やファイルの成否確認を意識してください。',
    条件分岐: '比較演算子と代入演算子の取り違え、条件式の優先順位、分岐漏れを確認してください。',
    ループ: '初期値・終了条件・更新式の組み合わせで、回数過不足や無限ループが起きないか確認してください。',
    関数: '引数、戻り値、関数の責務が一致しているかを確認してください。',
    配列: '添字の範囲、要素数、`sizeof` の意味が配列とポインタで変わる点を確認してください。',
    文字列: '終端文字 `\\0`、比較方法、書き込み可能な領域かどうかを確認してください。',
    ポインタ: 'アドレスと値の違い、NULL チェック、寿命切れポインタの扱いを確認してください。',
    メモリ: '`malloc` / `realloc` / `free` の戻り値と所有権、解放後の利用有無を確認してください。',
    ビット演算: 'ビットマスク、シフト、演算子の優先順位、符号の有無を確認してください。',
    データ構造: 'ノードや添字のつなぎ替え、push/pop 後の状態更新、空状態の扱いを確認してください。',
    アルゴリズム: '境界条件、比較関数、探索範囲の更新が正しいかを確認してください。',
    数値解析: '型変換、丸め誤差、収束条件、0除算や桁落ちに注意してください。',
    グラフィックス: '座標、色、描画順序、`update_frame()` の呼び出し位置を確認してください。'
  };

  return checkpointMap[category] || 'コード全体の前提条件と境界条件を確認してください。';
};

const buildExerciseGuide = ({
  title,
  description,
  starterCode,
  solution,
  category,
  type,
  format
}) => {
  const taskComments = extractTaskComments(starterCode);
  const tasks = [];
  const successCriteria = [];
  const checkpoints = [
    getCategoryCheckpoint(category),
    format === 'エラー探し'
      ? '「直したら動いた」で終わらせず、どの行がなぜ危険だったのか説明できる状態を目標にしてください。'
      : '完成したら入力例やテストデータを変えて、同じ考え方で処理できるかも確認してください。'
  ];

  if (format === 'エラー探し') {
    tasks.push('starterCode を読み、コンパイルエラー、実行時エラー、論理バグの候補を洗い出してください。');
    tasks.push(...taskComments.map(toSentence));
    tasks.push(
      type === 'graphics'
        ? '修正後に実行し、キャンバス上の見た目が意図どおりか確認してください。'
        : '修正後に実行し、出力やデータの状態が意図どおりか確認してください。'
    );
    successCriteria.push('必要な修正を入れたあと、コードが最後まで正常に実行できること。');
  } else {
    tasks.push('starterCode の TODO や未完成部分を埋め、途中で止まらないコードへ仕上げてください。');
    tasks.push(...taskComments.map(toSentence));
    tasks.push(
      type === 'graphics'
        ? '描画結果を見ながら、座標や色や更新順が狙いどおりになっているか確認してください。'
        : '必要に応じて入力値や途中結果を確認し、処理の流れを自分の言葉で追える状態にしてください。'
    );
    successCriteria.push('課題で求められた処理が実装され、starterCode の意図どおりに最後まで実行できること。');
  }

  if (type === 'graphics') {
    successCriteria.push('実行するとキャンバス上に、課題で求められた図形や動きが確認できること。');
    checkpoints.push('描画系の問題では、色設定とフレーム更新の順序を変えると見え方が大きく変わる点に注意してください。');
  } else if (solution.includes('scanf(')) {
    successCriteria.push('必要な入力を与えると、その値に応じた計算結果や判定結果が表示されること。');
  } else {
    successCriteria.push('実行すると、課題の成否を確認できる出力や結果が表示されること。');
  }

  if (solution.includes('malloc(') || solution.includes('calloc(') || solution.includes('realloc(')) {
    successCriteria.push('メモリ確保が必要な場合は失敗時の扱いも含めて安全に処理できていること。');
  }

  const expectations = buildExerciseExpectations({
    title,
    description,
    type,
    format,
    solution
  });

  return {
    overview:
      format === 'エラー探し'
        ? `${toSentence(description)} 配布されたコードには初心者が見落としやすいものから、条件が重なると表面化する見えにくい不具合まで含まれています。意図した処理を想像しながら、原因を切り分けて修正してください。`
        : `${toSentence(description)} starterCode の骨組みを手がかりに、自分で必要な処理を補って完成させる演習です。レッスンで見た構文を「読める」だけでなく「自力で書ける」状態を目指してください。`,
    tasks: unique(tasks).slice(0, 4),
    successCriteria: unique(successCriteria).slice(0, 4),
    checkpoints: unique(checkpoints).slice(0, 3),
    ...expectations
  };
};

const exercise = ({
  title,
  description,
  starterCode,
  solution,
  category = '発展',
  difficulty = '中級',
  type = 'standard',
  format = '実装',
  overview,
  tasks,
  successCriteria,
  checkpoints,
  starterCodeGuide,
  todoList,
  expectedBehavior,
  answerFormatNote,
  outputExample
}) => {
  const guide = buildExerciseGuide({
    title,
    description,
    starterCode,
    solution,
    category,
    type,
    format
  });

  return {
    title,
    description: toSentence(description),
    starterCode,
    solution,
    category,
    difficulty,
    type,
    format,
    overview: overview || guide.overview,
    tasks: tasks || guide.tasks,
    successCriteria: successCriteria || guide.successCriteria,
    checkpoints: checkpoints || guide.checkpoints,
    starterCodeGuide: starterCodeGuide || '',
    todoList: todoList || [],
    expectedBehavior: expectedBehavior || guide.expectedBehavior,
    answerFormatNote: answerFormatNote || guide.answerFormatNote,
    outputExample: outputExample || guide.outputExample
  };
};

const buildLessonStarterCodeGuide = (lesson, todoList) => {
  if (lesson.category === '関数') {
    return `この starterCode では、\`#include\` と \`main\` の枠だけを先に用意しています。関数系の演習なので、必要な関数プロトタイプ、関数定義、場合によってはグローバル変数を \`main\` の前へ自分で追加してください。編集順に迷わないよう、まず TODO 1 から TODO ${todoList.length} までを順に埋め、最後に \`main\` からその関数を呼び出して動作を確認してください。`;
  }

  if (lesson.category === '配列') {
    return `この starterCode は \`main\` の枠だけを残した最小構成です。配列問題では、配列宣言、添字、表示処理を自分で追加する必要があります。先に TODO を順に埋め、最後に添字が 0 始まりであることと、範囲外アクセスをしていないことを確認してください。`;
  }

  if (lesson.category === '入出力') {
    return `この starterCode では、必要な変数宣言、\`scanf\` / \`printf\` の行、計算や表示の順序を自分で組み立てます。TODO は「宣言」「入力」「表示」の順に並べてあるので、その順で埋めると処理の流れを追いやすくなります。`;
  }

  if (lesson.category === '条件分岐') {
    return `この starterCode は、条件式と分岐本体を自分で完成させる前提で用意しています。TODO を上から埋めると、「比較する値を用意する」「条件式を書く」「どの結果を表示すべきか決める」という順で組み立てられます。`;
  }

  if (lesson.category === 'ループ') {
    return `この starterCode では、反復に必要な変数、ループ条件、更新式、表示処理を自分で足していきます。TODO は「初期化」「繰り返し条件」「1回ごとの処理」を意識した順になっているので、実行前に無限ループにならないか確認してください。`;
  }

  if (lesson.category === '文字列') {
    return `この starterCode では、文字列を表す \`char\` 配列や文字列関数の使い方を自分で組み立てます。TODO は「文字列を用意する」「必要なら 1 文字ずつ処理する」「最後に結果を表示する」という順で読めるようにしてあります。終端文字 \`\\0\` と配列サイズの扱いを特に確認してください。`;
  }

  if (lesson.category === 'ポインタ') {
    return `この starterCode は、値・アドレス・間接参照の関係を自分で書き分ける前提です。TODO を順に埋めると「元の値を用意する」「ポインタを結び付ける」「\`*\` や \`&\` で読み書きする」という流れで組み立てられます。どの変数が値で、どれがアドレスかを意識して進めてください。`;
  }

  if (lesson.category === 'メモリ') {
    return `この starterCode では、動的メモリ確保の流れを自分で完成させます。TODO は「必要サイズを計算して確保する」「NULL を確認する」「使い終わったら解放する」という安全な順序を意識して並べています。メモリ確保系の演習では、最後の \`free\` まで書いてから完了と考えてください。`;
  }

  if (lesson.category === 'ビット演算') {
    return `この starterCode は、ビット演算子を試しながら結果の変化を確認する前提です。TODO を埋めると「値を用意する」「ビット演算やシフトを行う」「結果を表示して確認する」という順に完成します。括弧の位置と符号の有無で意味が変わるので、式を丁寧に読んでください。`;
  }

  if (lesson.category === 'データ構造') {
    return `この starterCode では、配列や構造体やポインタを使ってデータ構造の形を自分で組み立てます。TODO は「構造を用意する」「状態を更新する」「最後に取り出しや走査で確認する」という流れにしています。どの変数が先頭・末尾・現在位置を表すのかを明確にして進めてください。`;
  }

  if (lesson.category === 'アルゴリズム') {
    return `この starterCode は、探索や整列や再帰処理の骨組みを自分で埋める前提です。TODO を順に処理すると「入力データや初期状態を用意する」「中心となるループや再帰を書く」「結果を表示して正しさを確認する」という流れになります。境界条件と終了条件を特に意識してください。`;
  }

  if (lesson.category === '数値解析') {
    return `この starterCode では、近似計算のための関数、反復、累積計算を自分で組み立てます。TODO は「対象関数や初期値を決める」「反復または累積の式を書く」「近似結果を表示する」という順にしてあります。型変換や丸め誤差で結果が変わりやすいので、\`double\` を使う位置を意識してください。`;
  }

  if (lesson.category === 'グラフィックス') {
    return `この starterCode では、描画 API を使って図形や動きを自分で完成させます。TODO は「キャンバスを初期化する」「座標や色を決めて描画する」「最後に \`update_frame()\` で反映する」という順で確認できるようにしています。見た目がずれたら、座標と描画順をまず見直してください。`;
  }

  return `この starterCode は、\`#include\` と \`main\` の枠だけを先に用意した状態です。下の TODO を順番に埋めると、そのレッスンで学んだ構文を使ってコードを完成できるようにしてあります。必要な変数宣言、式、表示処理を自分で追加しながら仕上げてください。`;
};

const guidedLessonExercisePlans = {
  'プログラムの基本構造': [
    '`main` の中に 1 行の `printf` を書き、任意のメッセージを表示してください。',
    '表示文字列の末尾に `\\n` を付けて、実行結果が改行されるようにしてください。',
    '`main` の最後は `return 0;` の形を保ち、正常終了まで含めて完成させてください。'
  ],
  'コメントとコード整形': [
    '処理の意図が分かる単一行コメントを 1 つ以上追加してください。',
    '必要なら複数行コメントも使い、説明とコードを区別できるようにしてください。',
    'インデントと改行を整え、実行してもそのまま読めるレイアウトにしてください。'
  ],
  '変数の宣言と代入': [
    '整数型の変数を宣言し、最初の値を代入してください。',
    '同じ変数へ別の値を代入し直して、値が更新されることを確認してください。',
    '変更前または変更後の値が分かるように `printf` で表示してください。'
  ],
  '整数型の使い分け': [
    '`int`、`short`、`long` のいずれか 2 種類以上を宣言して値を入れてください。',
    '`sizeof` を使って、型ごとのサイズ差が分かる出力を追加してください。',
    '値そのものも表示し、型によって扱い方が変わることを確認してください。'
  ],
  '浮動小数点型の使い分け': [
    '`float` と `double` の変数を用意して小数値を代入してください。',
    '`printf` の書式指定で表示桁数を調整し、見え方を比べてください。',
    'どちらの型を使っているか分かるように出力内容を整理してください。'
  ],
  '文字型とASCII': [
    '`char` 型の変数へ 1 文字を代入してください。',
    '同じ値を `%c` と `%d` の両方で表示し、文字と数値の対応を確認してください。',
    '文字コードを使った簡単な計算を 1 つ入れ、別の文字へ変わる様子を表示してください。'
  ],
  '定数とconst': [
    '`const` を使って意味のある名前の定数を 1 つ定義してください。',
    'その定数を計算または表示処理で使い、値を直接書かない形にしてください。',
    'どの値が固定値なのか、コードを読んだときすぐ分かる名前にしてください。'
  ],
  '算術演算子': [
    '2 つの整数を用意し、`+`、`-`、`*`、`/`、`%` の結果を求めてください。',
    '整数除算では小数点以下が切り捨てられることが分かる出力を含めてください。',
    'どの演算結果なのか分かるように、`printf` の表示内容へラベルを付けてください。'
  ],
  '代入演算子': [
    '変数へ初期値を入れ、`+=` や `-=` などの複合代入を 2 種類以上使ってください。',
    '更新のたびに値を表示し、どの演算でどう変わったか追えるようにしてください。',
    '現在の値をもとに更新していることが分かる流れにしてください。'
  ],
  'インクリメントとデクリメント': [
    '`++` と `--` を使って、値が 1 ずつ増減するコードを書いてください。',
    '前置または後置のどちらかを使い、評価結果の違いが見える形にしてください。',
    '最後に値を表示し、何回更新されたかを確認できるようにしてください。'
  ],
  '比較演算子': [
    '比較に使う 2 つの値を用意してください。',
    '`==`、`!=`、`<`、`>` のいずれかを使って条件結果を作ってください。',
    '比較結果が分かるように `printf` または分岐で表示し、`=` と混同しない形にしてください。'
  ],
  '論理演算子': [
    '2 つ以上の条件を用意し、`&&`、`||`、`!` のいずれかを使って組み合わせてください。',
    'どの条件が真になったときに処理が進むか、コード上で読み取れる形にしてください。',
    '最終的な判定結果を表示し、条件の組み合わせが正しいか確認してください。'
  ],
  '演算子の優先順位': [
    '同じ値を使った式を 2 つ用意し、片方はそのまま、片方はかっこ付きで書いてください。',
    '計算順が変わることで結果差が出る形にしてください。',
    '2 つの結果を表示し、どこにかっこを入れるべきか分かるようにしてください。'
  ],
  '明示的な型変換': [
    '整数同士の割り算を 1 つ用意してください。',
    '`(double)` などのキャストを使い、実数計算に変わる形を追加してください。',
    'キャストなしの結果とありの結果を並べて表示してください。'
  ],
  'printfの書式指定': [
    '文字列、整数、小数を表す変数をそれぞれ 1 つずつ用意してください。',
    '`printf` の書式指定子を使い分け、1 行または複数行で表示してください。',
    '小数は桁数指定も入れ、見せたい精度で表示してください。'
  ],
  'scanfで整数入力': [
    '整数を受け取る変数を宣言してください。',
    '入力を促すメッセージを出したあと、`scanf` で 1 つの整数を読み込んでください。',
    '読み取った値をそのまま表示し、入力結果が変数へ入ったことを確認してください。'
  ],
  'scanfで複数入力': [
    '複数の整数を保存する変数を用意してください。',
    '1 回の `scanf` で 2 つ以上の値を読み込んでください。',
    '読み取った値を使って合計などの簡単な計算を行い、結果を表示してください。'
  ],
  'if文': [
    '条件判定に使う値を 1 つ用意してください。',
    '`if` の条件式を書き、条件が真のときだけ実行される処理を追加してください。',
    '真になったときのメッセージや結果が確認できるように表示してください。'
  ],
  'if-else文': [
    '分岐に使う値を 1 つ決めてください。',
    '`if` と `else` の両方を書き、真と偽で違う結果が出るようにしてください。',
    '実行時にどちらか一方だけ表示されることを確認してください。'
  ],
  'else-if連鎖': [
    '複数の範囲や条件を判定する値を 1 つ用意してください。',
    '`if`、`else if`、`else` を使い、上から順に条件を判定してください。',
    'どの条件に入ったかが分かる評価結果を表示してください。'
  ],
  'switch文': [
    '`switch` で分岐する整数または文字の値を用意してください。',
    '`case` を複数書き、それぞれで異なる処理や表示を入れてください。',
    '`break` と `default` も含め、想定外の値でも終われる形にしてください。'
  ],
  '条件演算子': [
    '判定に使う値を 1 つ用意してください。',
    '`条件 ? 真の値 : 偽の値` の形で結果を 1 つの式として代入してください。',
    '選ばれた結果を表示し、短い分岐として成立しているか確認してください。'
  ],
  'while文': [
    'ループ用の変数を初期化してください。',
    '`while` の条件式を書き、繰り返しごとに表示する処理を入れてください。',
    '更新式も忘れずに書き、無限ループにならない形で終わるようにしてください。'
  ],
  'do-while文': [
    '繰り返しに使う変数を初期化してください。',
    '`do { ... } while (...);` の形で、本体が先に 1 回実行されるコードを書いてください。',
    '条件が偽になるまで値を更新し、少なくとも 1 回は表示されることを確認してください。'
  ],
  'for文': [
    '`for` の初期化・条件・更新の 3 つを 1 行で書いてください。',
    '1 周ごとに値を表示する処理を入れてください。',
    '開始値と終了条件を見直し、回数が過不足なくなるようにしてください。'
  ],
  'ネストしたループ': [
    '外側のループで行、内側のループで列を表す変数を用意してください。',
    '内側で記号や数値を表示し、外側の 1 周ごとに改行してください。',
    '何行何列のパターンを作るのか、ループ条件から読み取れるようにしてください。'
  ],
  'break文': [
    '繰り返し処理を 1 つ書いてください。',
    '途中で終了したい条件を `if` で判定し、その中で `break` を使ってください。',
    '終了条件より後の値が表示されないことを確認してください。'
  ],
  'continue文': [
    '繰り返し処理を 1 つ書いてください。',
    '飛ばしたい条件を `if` で判定し、その中で `continue` を使ってください。',
    '除外した値だけ表示されない結果になるようにしてください。'
  ],
  '番兵値を使うループ': [
    '終了用の特別な値を 1 つ決め、ループ条件に組み込んでください。',
    '`scanf` で値を読み取り、番兵値でなければ処理を続ける流れにしてください。',
    '番兵値が入力された時点で自然に終了することを確認してください。'
  ],
  '関数の宣言と定義': [
    '`main` の前に、役割が分かる名前の `void` 関数を 1 つ定義してください。',
    'その関数の中へ表示処理などの役割をまとめてください。',
    '`main` から同じ関数を複数回呼び出し、再利用できていることを確認してください。'
  ],
  '関数プロトタイプ': [
    '`main` より前に関数プロトタイプを書いてください。',
    'その後で同じ関数の定義を書き、引数と戻り値の型を一致させてください。',
    '`main` から関数を呼び出して戻り値を表示してください。'
  ],
  '引数と戻り値': [
    '引数を受け取って計算する関数を 1 つ定義してください。',
    '計算結果を `return` で呼び出し元へ返してください。',
    '`main` から具体的な値を渡して関数を呼び出し、戻り値を表示してください。'
  ],
  'void関数': [
    '戻り値を持たない `void` 関数を 1 つ定義してください。',
    'その関数の中に表示や区切り線などの処理だけを書いてください。',
    '`main` から必要な回数呼び出し、表示専用の関数として使えているか確認してください。'
  ],
  '値渡しの理解': [
    '引数を 1 つ受け取る関数を定義してください。',
    '関数の中で引数の値を変更して表示し、コピー側だけが変わることを確認してください。',
    '`main` 側の元の変数も表示し、元の値が変わっていないことを比べてください。'
  ],
  'グローバル変数とローカル変数': [
    '`main` の外にグローバル変数を 1 つ定義してください。',
    'ローカル変数または引数を使う関数を定義し、その中でグローバル変数を更新してください。',
    '`main` から関数を複数回呼び出し、最終的な値を表示してください。'
  ],
  'staticローカル変数': [
    '関数の中に `static` なローカル変数を 1 つ用意してください。',
    '関数が呼ばれるたびにその値を更新して表示してください。',
    '`main` から同じ関数を複数回呼び出し、値が保持されることを確認してください。'
  ],
  '再帰の基本': [
    '自分自身を呼び出す関数を 1 つ定義してください。',
    '必ず止まるための終了条件を最初に書いてください。',
    '1 つ小さい問題へ進む再帰呼び出しを書き、`main` から結果を表示してください。'
  ],
  'ユークリッドの互除法': [
    '2 つの整数を受け取る `gcd` 関数を定義してください。',
    '`b == 0` の終了条件と、`gcd(b, a % b)` の再帰呼び出しを実装してください。',
    '`main` から具体例で呼び出し、最大公約数が表示されるようにしてください。'
  ],
  '関数分割による設計': [
    '役割の異なる小さな関数を 2 つ以上定義してください。',
    'それぞれの関数は 1 つの責務だけを持つように実装してください。',
    '`main` ではそれらを組み合わせて最終結果を作り、表示してください。'
  ],
  '一次元配列': [
    '整数配列を 1 つ宣言し、複数の値を初期化してください。',
    '添字 0 と最後の添字を使って、複数の要素へアクセスしてください。',
    '配列の範囲を超えないことを意識しながら、取得した値を表示してください。'
  ],
  '配列の初期化': [
    '要素数を明示するか省略するかを決めて、整数配列を波かっこで初期化してください。',
    '初期化した値が順に入っていることを確認できるよう、添字またはループで取り出してください。',
    '全要素を表示し、配列宣言と初期化が 1 つの文で完結している形にしてください。'
  ],
  '配列の集計': [
    '複数要素を持つ配列と、合計を入れる変数を用意してください。',
    '`for` 文で全要素を順に走査し、各要素を合計へ加算してください。',
    '必要なら平均も求め、実数除算になるよう意識して結果を表示してください。'
  ],
  '最大値と最小値の探索': [
    '探索対象の配列を用意し、最初の要素を最大値・最小値の初期値にしてください。',
    '2 番目以降の要素を順に比較し、より大きい値や小さい値が見つかったら更新してください。',
    '最後に最大値と最小値の両方を表示し、探索結果を確認してください。'
  ],
  '二次元配列': [
    '行と列を持つ二次元配列を宣言し、値を初期化してください。',
    '行番号と列番号の 2 つの添字を使って、特定の要素へアクセスしてください。',
    '取り出した値を表示し、どの位置を参照しているか分かるようにしてください。'
  ],
  '行列の走査': [
    '二次元配列を用意し、走査に使う行・列のループ変数を決めてください。',
    '外側と内側の二重ループで全要素を順に取り出してください。',
    '行ごとに改行を入れるなどして、行列らしい形で表示してください。'
  ],
  '文字列の基本': [
    '`char` 配列を文字列リテラルで初期化してください。',
    '`%s` で文字列全体を表示し、必要なら添字を使って 1 文字も取り出してください。',
    '文字列が文字の配列であることが分かる出力にしてください。'
  ],
  '文字列長の計算': [
    '文字列を用意し、長さを数えるための変数を 0 で初期化してください。',
    '終端文字 `\\0` に当たるまで 1 文字ずつ進めて長さを数えてください。',
    '求めた長さを表示し、`strlen` を使わずに数えられていることを確認してください。'
  ],
  'string.hの活用': [
    '`string.h` の関数を使う前提で、十分な大きさの文字配列を用意してください。',
    '`strcpy`、`strcat`、`strlen` のうち 1 つ以上を使って文字列を加工または確認してください。',
    '処理後の文字列と長さを表示し、ライブラリ関数の効果が分かるようにしてください。'
  ],
  '文字分類と変換': [
    '判定や変換の対象となる文字を 1 つ用意してください。',
    '`ctype.h` の関数で、英字判定や大文字変換などを 1 つ以上行ってください。',
    '判定結果または変換結果を表示し、元の文字との違いを確認してください。'
  ],
  '文字列配列': [
    '複数の単語を保存する 2 次元 `char` 配列を用意してください。',
    '添字を使って各単語へアクセスできるようにしてください。',
    'ループで全単語を表示し、文字列の一覧として扱えていることを確認してください。'
  ],
  'コマンドライン引数': [
    '`main` の仮引数を `argc` と `argv` を使う形へ変更してください。',
    '`argc` を表示し、受け取った引数の個数が分かるようにしてください。',
    '`argv[i]` をループで順に表示し、起動時に渡した文字列が読めるようにしてください。'
  ],
  'ポインタの基礎': [
    '整数変数を 1 つ用意し、そのアドレスを保存するポインタを宣言してください。',
    '`&` でアドレスを取得し、`*` で指している先の値を読み取ってください。',
    'アドレスと値の両方を表示し、ポインタが何を保持しているか確認してください。'
  ],
  'アドレス演算子と間接参照': [
    '元の変数と、それを指すポインタを用意してください。',
    'ポインタ経由の代入で元の変数の値を更新してください。',
    '最後に元の変数を表示し、間接参照で値が書き換わったことを確認してください。'
  ],
  'ポインタと配列': [
    '配列を用意し、配列名をポインタのように使うコードを書いてください。',
    '`array[i]` に対応する `*(array + i)` の形で要素を 1 つ以上取り出してください。',
    '同じ要素が両方の書き方で読めることを表示して確認してください。'
  ],
  'ポインタ演算': [
    '配列と、その先頭を指すポインタを用意してください。',
    'ポインタへオフセットを足して、先頭以外の要素も順に参照してください。',
    '走査結果を表示し、型サイズ単位で進んでいることを確認してください。'
  ],
  '関数へのポインタ渡し': [
    'ポインタ引数を受け取る関数を 1 つ定義してください。',
    'その関数内で間接参照を使い、呼び出し元の値を更新してください。',
    '`main` からアドレスを渡して呼び出し、更新後の値を表示してください。'
  ],
  'swapの実装': [
    '2 つの整数ポインタを受け取る `swap` 関数を定義してください。',
    '一時変数を使って、指している先の値を交換してください。',
    '`main` で 2 つの変数を用意して呼び出し、交換後の値を表示してください。'
  ],
  'charポインタと文字列': [
    '`char *` で文字列を指すポインタを用意してください。',
    'ポインタを 1 文字ずつ進めながら、終端文字まで読み取ってください。',
    '各文字または文字列全体を表示し、ポインタ移動で読めることを確認してください。'
  ],
  'malloc': [
    '必要な要素数と `sizeof` を使って、動的配列を確保してください。',
    '`NULL` を確認したうえで、確保した領域へ値を書き込んで表示してください。',
    '使い終わったら `free` を呼び、解放まで含めて完成させてください。'
  ],
  'callocとfree': [
    '`calloc` を使って 0 初期化された領域を確保してください。',
    '確保した配列の一部へ値を入れ、読み出して表示してください。',
    '最後に `free` で解放し、確保から解放までの流れを完了してください。'
  ],
  'realloc': [
    '最初に小さめの動的配列を確保し、初期値を入れてください。',
    '`realloc` の戻り値を一時ポインタで受け、失敗時に元ポインタを失わない形にしてください。',
    'サイズ変更後の領域へ追加の値を入れて表示し、最後に解放してください。'
  ],
  'ポインタの配列と配列へのポインタ': [
    'ポインタを並べた配列と、配列全体を指すポインタの両方を用意してください。',
    'それぞれの構文で何を指しているかが分かるよう、値を 1 つ以上取り出してください。',
    '表示結果を通して、括弧の位置で意味が変わることを確認してください。'
  ],
  '二重ポインタ': [
    '値、値を指すポインタ、そのポインタを指す二重ポインタを順に用意してください。',
    '`**` を使って最終的な値を読み書きしてください。',
    '更新後の元の変数を表示し、二重にたどっても同じ値へ届くことを確認してください。'
  ],
  '関数ポインタ': [
    '通常の関数を 1 つ定義したあと、その関数を指す関数ポインタを宣言してください。',
    '関数ポインタへ関数を代入し、そのポインタ経由で呼び出してください。',
    '結果を表示し、関数を値のように渡せていることを確認してください。'
  ],
  'voidポインタ': [
    '具体的な型の変数を用意し、そのアドレスを `void *` に入れてください。',
    '使う直前に元の型へキャストし、値を取り出してください。',
    '表示結果を通して、`void *` はそのままでは逆参照できないことも意識してください。'
  ],
  'sizeofとメモリレイアウト': [
    '配列や構造体など、サイズを比べたい対象を 2 種類以上用意してください。',
    '`sizeof` を使って全体サイズや型サイズを取得してください。',
    '表示結果から要素数や構造の大きさが読み取れるようにしてください。'
  ],
  'ビット表現の基礎': [
    'ビット演算の対象となる整数値を用意してください。',
    'その値と、右シフトなどで変化させた値を表示してください。',
    'ビット操作により数値がどう変わるか比較できるようにしてください。'
  ],
  'ビットAND・OR・XOR': [
    '2 つの整数値を用意し、AND・OR・XOR をそれぞれ計算してください。',
    '3 つの結果を別々に表示し、演算子ごとの差が分かるようにしてください。',
    'どのビットが残るか、どのビットが立つかを確認できる出力にしてください。'
  ],
  'ビットシフト': [
    'シフト対象となる整数値を 1 つ用意してください。',
    '左シフトと右シフトの両方を 1 回以上行ってください。',
    'シフト前後の値を表示し、桁移動による変化を確認してください。'
  ],
  'ビットマスク': [
    '状態を入れる整数変数と、確認したいビット位置を決めてください。',
    'OR でビットを立て、AND でそのビットが立っているか確認してください。',
    '設定結果と判定結果を表示し、マスク操作の流れが分かるようにしてください。'
  ],
  'フラグ集合の設計': [
    '複数の状態を表すビット定数または `enum` を定義してください。',
    '1 つの整数へ複数フラグをまとめて設定してください。',
    '各フラグの有無を個別に判定して表示し、集合として扱えていることを確認してください。'
  ],
  'ビットフィールド': [
    'ビット幅付きメンバを持つ構造体を定義してください。',
    '各フィールドへ値を代入し、通常の構造体メンバと同じようにアクセスしてください。',
    '表示結果から、狭いビット幅でも状態を保持できることを確認してください。'
  ],
  '2の補数と符号表現': [
    '正数と負数を 1 つずつ用意してください。',
    '符号付き整数としての値やビット演算後の値を表示してください。',
    '負数の扱いで予想と違う結果にならないか、符号付きであることを意識して確認してください。'
  ],
  '固定小数点の考え方': [
    '小数を整数で近似するための倍率を 1 つ決めてください。',
    '実数の代わりに倍率付き整数へ変換し、加減乗除の一部を行ってください。',
    '最後に元のスケールへ戻した値を表示し、固定小数点の考え方を確認してください。'
  ],
  'ビット列の1の個数': [
    '対象となる整数値を用意し、ビット数を数える変数を 0 で初期化してください。',
    'シフトと AND を使って下位ビットを順に調べ、1 の個数を加算してください。',
    '最終的な個数を表示し、popcount の流れを確認してください。'
  ],
  '構造体の基本': [
    '複数の型をまとめた構造体を定義してください。',
    '構造体変数を 1 つ用意し、各メンバへ値を代入してください。',
    'メンバを表示し、1 つのまとまりとして扱えていることを確認してください。'
  ],
  'ネストした構造体': [
    '構造体の中へ別の構造体を入れた定義を書いてください。',
    '外側と内側のメンバへ値を設定してください。',
    'ドット演算子を連ねて内側の値まで表示し、階層構造を確認してください。'
  ],
  'typedefとenum': [
    '`typedef` で型名の別名を作り、`enum` で意味のある定数を定義してください。',
    'その型や列挙子を使って変数を宣言してください。',
    '値を代入して表示し、読みやすい型名と状態表現になっていることを確認してください。'
  ],
  'union': [
    '複数の型を共有する `union` を定義してください。',
    'いずれか 1 つのメンバへ値を書き込み、同じメモリを別の見方で読む形も試してください。',
    '最後に表示し、構造体との違いが分かるようにしてください。'
  ],
  '単方向リスト': [
    '値と次ノードポインタを持つ構造体を定義してください。',
    '複数ノードをつなぎ、先頭から末尾までたどれる形にしてください。',
    'ループで全ノードの値を表示し、`next` をたどる流れを確認してください。'
  ],
  '双方向リスト': [
    '前後ポインタを持つノード構造体を定義してください。',
    '2 つ以上のノードをつなぎ、`prev` と `next` の両方が正しく張られるようにしてください。',
    '前後どちらかから値をたどって表示し、双方向に移動できることを確認してください。'
  ],
  'スタック': [
    '配列と `top` を使ってスタックの状態を管理してください。',
    'push を複数回行ったあと、pop で最後に入れた値を取り出してください。',
    '取り出した結果を表示し、後入れ先出しになっていることを確認してください。'
  ],
  'キュー': [
    '配列と `front`、`rear` を用意してキューの状態を管理してください。',
    'enqueue で値を 2 つ以上追加し、dequeue で先頭から取り出してください。',
    '表示結果を通して、先入れ先出しの順になっていることを確認してください。'
  ],
  'リングバッファ': [
    '固定長配列と `head`、`tail` を用意してください。',
    'インデックスを剰余で循環させながら、値を追加して取り出してください。',
    '配列を再利用しながら順番どおりに取り出せることを表示で確認してください。'
  ],
  '二分探索木': [
    '値、左部分木、右部分木を持つノード構造体を定義してください。',
    '根・左子・右子を持つ簡単な木を組み立ててください。',
    '親と子の値を表示し、左が小さく右が大きい配置になっていることを確認してください。'
  ],
  'ヒープ': [
    'ヒープを表す配列を用意してください。',
    '根、左子、右子に当たる添字の値を取り出してください。',
    '表示結果から、親子関係を配列添字で表せることを確認してください。'
  ],
  'グラフ表現': [
    '隣接行列として 2 次元配列を用意してください。',
    '辺がある組み合わせとない組み合わせを、添字で確認してください。',
    '表示結果から、頂点同士の接続関係が読み取れるようにしてください。'
  ],
  'グラフィックス表現': [
    '`clear_canvas()` で描画前のキャンバスを初期化してください。',
    'ループや座標計算を使って、複数の図形や色を画面へ描いてください。',
    '最後に `update_frame()` を呼び、完成した図形が表示されるようにしてください。'
  ],
  '線形探索': [
    '探索対象の配列と探したい値を用意してください。',
    '先頭から順に比較し、見つかった位置を記録するループを書いてください。',
    '見つかった場合と見つからない場合の両方を意識して結果を表示してください。'
  ],
  '二分探索': [
    '整列済み配列、探索対象、`low`、`high` を用意してください。',
    '`mid` を計算しながら探索範囲を半分ずつ絞り込んでください。',
    '目的の値が見つかった位置を表示し、整列済み前提で動いていることを確認してください。'
  ],
  'バブルソート': [
    '未整列の配列を 1 つ用意してください。',
    '外側と内側のループで隣接要素を比較し、必要なら交換してください。',
    '最後に全要素を表示し、昇順になっていることを確認してください。'
  ],
  '選択ソート': [
    '未整列の配列を用意し、各周回の開始位置を決めてください。',
    '未整列部分から最小値の位置を探し、先頭要素と交換してください。',
    '整列後の配列を表示し、1 周ごとに 1 要素ずつ確定していることを確認してください。'
  ],
  '挿入ソート': [
    '配列の 2 番目以降を順に取り出すループを書いてください。',
    '挿入したい値を一時保存し、より大きい要素を右へずらしてください。',
    '空いた位置へ値を挿入し、最終的な整列結果を表示してください。'
  ],
  'マージソート': [
    '配列を左右へ分けて再帰的に処理する関数を用意してください。',
    '2 つの整列済み区間を一時配列へマージする処理を書いてください。',
    '最終的に整列した配列を表示し、分割統治で動いていることを確認してください。'
  ],
  'クイックソート': [
    '区間の左右端と pivot を使う再帰関数を用意してください。',
    '左右から走査して pivot 基準で要素を交換する処理を書いてください。',
    '分割後に左右の区間へ再帰し、整列結果を表示してください。'
  ],
  '幅優先探索': [
    '隣接行列、キュー、訪問済み配列を用意してください。',
    '開始頂点をキューへ入れ、先頭から取り出しながら隣接頂点を追加してください。',
    '訪問順または到達判定を表示し、キューで近い順に進んでいることを確認してください。'
  ],
  '深さ優先探索': [
    '訪問済み配列と再帰 DFS 関数を用意してください。',
    '現在頂点を訪問済みにしたあと、未訪問の隣接頂点へ再帰してください。',
    '訪問順を表示し、深く潜ってから戻る流れを確認してください。'
  ],
  '動的計画法の基礎': [
    '部分問題の結果を保存する配列を用意してください。',
    '初期条件を先に入れ、その後ろを漸化式に従って順に埋めてください。',
    '最後に求めたい位置の値を表示し、再利用によって計算できていることを確認してください。'
  ],
  '数値積分': [
    '積分対象の関数、区間、分割数、刻み幅を用意してください。',
    '各小区間の面積を台形公式で求め、合計へ加算してください。',
    '近似積分値を表示し、分割によって連続関数を近似していることを確認してください。'
  ],
  'ニュートン法': [
    '関数 `f` と導関数 `df`、初期値を用意してください。',
    '反復式 `x = x - f(x) / df(x)` を決めた回数だけ適用してください。',
    '最後に近似解を表示し、反復で解へ近づいていることを確認してください。'
  ]
};

const lessonExercises = lessons.slice(0, 100).map((lesson) =>
  {
    const lessonSummary = extractLeadSentence(lesson.description);
    const todoList = guidedLessonExercisePlans[lesson.title] || [];
    const starterCodeGuide =
      todoList.length > 0 ? buildLessonStarterCodeGuide(lesson, todoList) : '';

    return exercise({
      title: `${lesson.title} 実践`,
      description: `レッスン「${lesson.title}」で学んだ考え方を使って取り組む演習です`,
      starterCode: buildLessonStarter(lesson, todoList),
      solution: lesson.code,
      category: lesson.category,
      difficulty: lesson.difficulty,
      type: lesson.type === 'graphics' ? 'graphics' : 'standard',
      overview:
        lesson.type === 'graphics'
          ? `レッスン「${lesson.title}」で扱った ${lessonSummary} を、自分の手で描画コードとして組み立て直す演習です。サンプルを写すだけでなく、どの API がどの見た目に対応しているかを確認しながら完成させてください。`
          : `レッスン「${lesson.title}」で扱った ${lessonSummary} を、自分の手で組み立て直す演習です。サンプルの流れを思い出しながら、どの変数・条件・ループ・関数が必要になるかを考えて完成させてください。`,
      starterCodeGuide,
      todoList
    });
  }
);

const rawAdvancedExercises = [
  exercise({
    title: '3つの入力値の平均',
    description: '3つの整数を読み込み、平均値を小数で表示してください。',
    starterCode: program({
      mainBody: `int a;
int b;
int c;

// 3つの整数を読み込み、平均を表示してください`
    }),
    solution: program({
      mainBody: `int a;
int b;
int c;

scanf("%d %d %d", &a, &b, &c);
printf("%.2f\\n", (a + b + c) / 3.0);`
    })
  }),
  exercise({
    title: 'うるう年判定',
    description: '西暦を1つ受け取り、うるう年かどうかを表示してください。',
    starterCode: program({
      mainBody: `int year;

// year を読み込み、うるう年なら "leap"、そうでなければ "common" を表示してください`
    }),
    solution: program({
      mainBody: `int year;

scanf("%d", &year);
if ((year % 400 == 0) || (year % 4 == 0 && year % 100 != 0)) {
    printf("leap\\n");
} else {
    printf("common\\n");
}`
    })
  }),
  exercise({
    title: '九九表の作成',
    description: '入れ子のループで9×9の掛け算表を表示してください。',
    starterCode: program({
      mainBody: `// 1から9までの九九表を表示してください`
    }),
    solution: program({
      mainBody: `for (int row = 1; row <= 9; row++) {
    for (int col = 1; col <= 9; col++) {
        printf("%2d ", row * col);
    }
    printf("\\n");
}`
    })
  }),
  exercise({
    title: '素数判定関数',
    description: '整数を受け取って素数かどうかを返す関数を作成してください。',
    starterCode: program({
      helpers: `int is_prime(int n) {
    // ここを実装してください
}`,
      mainBody: `printf("%d\\n", is_prime(29));`
    }),
    solution: program({
      helpers: `int is_prime(int n) {
    if (n < 2) {
        return 0;
    }
    for (int i = 2; i * i <= n; i++) {
        if (n % i == 0) {
            return 0;
        }
    }
    return 1;
}`,
      mainBody: `printf("%d\\n", is_prime(29));`
    })
  }),
  exercise({
    title: '配列の右回転',
    description: '整数配列を1つ右へローテーションしてください。',
    starterCode: program({
      mainBody: `int values[5] = {1, 2, 3, 4, 5};

// 配列を右に1つ回転してください`
    }),
    solution: program({
      mainBody: `int values[5] = {1, 2, 3, 4, 5};
int last = values[4];

for (int i = 4; i > 0; i--) {
    values[i] = values[i - 1];
}
values[0] = last;

for (int i = 0; i < 5; i++) {
    printf("%d\\n", values[i]);
}`
    })
  }),
  exercise({
    title: '行列の対角和',
    description: '3x3行列の主対角要素の合計を求めてください。',
    starterCode: program({
      mainBody: `int matrix[3][3] = {
    {1, 2, 3},
    {4, 5, 6},
    {7, 8, 9}
};

// 主対角要素の和を求めて表示してください`
    }),
    solution: program({
      mainBody: `int matrix[3][3] = {
    {1, 2, 3},
    {4, 5, 6},
    {7, 8, 9}
};
int total = 0;

for (int i = 0; i < 3; i++) {
    total += matrix[i][i];
}

printf("%d\\n", total);`
    })
  }),
  exercise({
    title: '文字列の逆順表示',
    description: '文字列を後ろから順に表示してください。',
    starterCode: program({
      mainBody: `char text[] = "algorithm";

// text を逆順に表示してください`
    }),
    solution: program({
      mainBody: `char text[] = "algorithm";
int length = 0;

while (text[length] != '\\0') {
    length++;
}

for (int i = length - 1; i >= 0; i--) {
    printf("%c", text[i]);
}
printf("\\n");`
    })
  }),
  exercise({
    title: 'ポインタで最大値を返す',
    description: '最大値を出力引数で返す関数を作成してください。',
    starterCode: program({
      helpers: `void find_max(int values[], int size, int *result) {
    // ここを実装してください
}`,
      mainBody: `int values[5] = {12, 7, 18, 5, 11};
int max_value;

find_max(values, 5, &max_value);
printf("%d\\n", max_value);`
    }),
    solution: program({
      helpers: `void find_max(int values[], int size, int *result) {
    *result = values[0];
    for (int i = 1; i < size; i++) {
        if (values[i] > *result) {
            *result = values[i];
        }
    }
}`,
      mainBody: `int values[5] = {12, 7, 18, 5, 11};
int max_value;

find_max(values, 5, &max_value);
printf("%d\\n", max_value);`
    })
  }),
  exercise({
    title: '動的配列の平均',
    description: '`malloc` で確保した配列の平均値を求めてください。',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      mainBody: `int *values = malloc(4 * sizeof(int));

// 動的配列へ値を入れて平均を表示してください`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      mainBody: `int *values = malloc(4 * sizeof(int));
int total = 0;

if (values == NULL) {
    return 1;
}

values[0] = 10;
values[1] = 20;
values[2] = 30;
values[3] = 40;

for (int i = 0; i < 4; i++) {
    total += values[i];
}

printf("%.1f\\n", total / 4.0);
free(values);`,
      addReturn: true
    })
  }),
  exercise({
    title: 'ビットフラグで権限判定',
    description: '読み取り・書き込み・実行権限をビットフラグで管理してください。',
    starterCode: program({
      mainBody: `unsigned int perms = 0;

// 読み取りと実行を許可し、各権限の有無を表示してください`
    }),
    solution: program({
      helpers: `enum Permission {
    READ_FLAG = 1 << 0,
    WRITE_FLAG = 1 << 1,
    EXEC_FLAG = 1 << 2
};`,
      mainBody: `unsigned int perms = 0;

perms |= READ_FLAG;
perms |= EXEC_FLAG;

printf("%d\\n", (perms & READ_FLAG) != 0);
printf("%d\\n", (perms & WRITE_FLAG) != 0);
printf("%d\\n", (perms & EXEC_FLAG) != 0);`
    })
  }),
  exercise({
    title: 'popcountの実装',
    description: '整数に含まれる1ビットの個数を数えてください。',
    starterCode: program({
      mainBody: `unsigned int value = 173;

// 1ビットの数を求めて表示してください`
    }),
    solution: program({
      mainBody: `unsigned int value = 173;
int count = 0;

while (value != 0) {
    count += value & 1u;
    value >>= 1;
}

printf("%d\\n", count);`
    })
  }),
  exercise({
    title: '単方向リストへの先頭挿入',
    description: '新しいノードをリスト先頭へ追加してください。',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      helpers: `struct Node {
    int value;
    struct Node *next;
};`,
      mainBody: `struct Node *head = NULL;

// 値 30, 20, 10 の順になるように先頭挿入してください`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      helpers: `struct Node {
    int value;
    struct Node *next;
};`,
      mainBody: `struct Node *head = NULL;
int values[3] = {10, 20, 30};

for (int i = 0; i < 3; i++) {
    struct Node *node = malloc(sizeof(struct Node));
    if (node == NULL) {
        return 1;
    }
    node->value = values[i];
    node->next = head;
    head = node;
}

for (struct Node *cur = head; cur != NULL; cur = cur->next) {
    printf("%d\\n", cur->value);
}`,
      addReturn: true
    })
  }),
  exercise({
    title: 'スタックのpushとpop',
    description: '配列で簡易スタックを作り、push/pop を試してください。',
    starterCode: program({
      mainBody: `int stack[5];
int top = 0;

// 10, 20, 30 を push し、1つ pop して表示してください`
    }),
    solution: program({
      mainBody: `int stack[5];
int top = 0;

stack[top++] = 10;
stack[top++] = 20;
stack[top++] = 30;

printf("%d\\n", stack[--top]);`
    })
  }),
  exercise({
    title: 'リングバッファの基本操作',
    description: 'リングバッファへ2つ追加して2つ取り出してください。',
    starterCode: program({
      mainBody: `int buffer[4] = {0};
int head = 0;
int tail = 0;

// enqueue と dequeue を模した処理を書いてください`
    }),
    solution: program({
      mainBody: `int buffer[4] = {0};
int head = 0;
int tail = 0;

buffer[tail] = 11;
tail = (tail + 1) % 4;
buffer[tail] = 22;
tail = (tail + 1) % 4;

printf("%d\\n", buffer[head]);
head = (head + 1) % 4;
printf("%d\\n", buffer[head]);`
    })
  }),
  exercise({
    title: '二分探索関数',
    description: '整列済み配列から値を探す二分探索関数を作成してください。',
    starterCode: program({
      helpers: `int binary_search(int values[], int size, int target) {
    // ここを実装してください
}`,
      mainBody: `int values[7] = {3, 8, 12, 15, 19, 27, 31};
printf("%d\\n", binary_search(values, 7, 19));`
    }),
    solution: program({
      helpers: `int binary_search(int values[], int size, int target) {
    int low = 0;
    int high = size - 1;

    while (low <= high) {
        int mid = (low + high) / 2;
        if (values[mid] == target) {
            return mid;
        }
        if (values[mid] < target) {
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    return -1;
}`,
      mainBody: `int values[7] = {3, 8, 12, 15, 19, 27, 31};
printf("%d\\n", binary_search(values, 7, 19));`
    })
  }),
  exercise({
    title: '2配列のマージ',
    description: '整列済み2配列を1つの整列済み配列へマージしてください。',
    starterCode: program({
      mainBody: `int a[3] = {1, 4, 7};
int b[4] = {2, 3, 8, 9};
int merged[7];

// 2つの配列をマージしてください`
    }),
    solution: program({
      mainBody: `int a[3] = {1, 4, 7};
int b[4] = {2, 3, 8, 9};
int merged[7];
int i = 0;
int j = 0;
int k = 0;

while (i < 3 && j < 4) {
    if (a[i] <= b[j]) {
        merged[k++] = a[i++];
    } else {
        merged[k++] = b[j++];
    }
}
while (i < 3) {
    merged[k++] = a[i++];
}
while (j < 4) {
    merged[k++] = b[j++];
}

for (int idx = 0; idx < 7; idx++) {
    printf("%d\\n", merged[idx]);
}`
    })
  }),
  exercise({
    title: 'BFSで到達判定',
    description: '開始頂点から到達可能かどうかを BFS で判定してください。',
    starterCode: program({
      mainBody: `int graph[5][5] = {
    {0, 1, 0, 0, 0},
    {1, 0, 1, 1, 0},
    {0, 1, 0, 0, 0},
    {0, 1, 0, 0, 1},
    {0, 0, 0, 1, 0}
};

// 頂点0から4へ到達可能かを表示してください`
    }),
    solution: program({
      mainBody: `int graph[5][5] = {
    {0, 1, 0, 0, 0},
    {1, 0, 1, 1, 0},
    {0, 1, 0, 0, 0},
    {0, 1, 0, 0, 1},
    {0, 0, 0, 1, 0}
};
int queue[5];
int visited[5] = {0};
int front = 0;
int rear = 0;

queue[rear++] = 0;
visited[0] = 1;

while (front < rear) {
    int node = queue[front++];
    for (int next = 0; next < 5; next++) {
        if (graph[node][next] && !visited[next]) {
            visited[next] = 1;
            queue[rear++] = next;
        }
    }
}

printf("%d\\n", visited[4]);`
    })
  }),
  exercise({
    title: '台形公式で積分',
    description: 'x^2 を 0 から 1 まで台形公式で積分してください。',
    starterCode: program({
      helpers: `double f(double x) {
    return x * x;
}`,
      mainBody: `// 台形公式で近似積分値を求めてください`
    }),
    solution: program({
      helpers: `double f(double x) {
    return x * x;
}`,
      mainBody: `double a = 0.0;
double b = 1.0;
int n = 1000;
double h = (b - a) / n;
double sum = 0.0;

for (int i = 0; i < n; i++) {
    double x1 = a + i * h;
    double x2 = x1 + h;
    sum += (f(x1) + f(x2)) * h / 2.0;
}

printf("%.6f\\n", sum);`
    })
  }),
  exercise({
    title: 'ニュートン法で平方根',
    description: 'ニュートン法で √3 を近似計算してください。',
    starterCode: program({
      mainBody: `// ニュートン法で sqrt(3) を近似してください`
    }),
    solution: program({
      mainBody: `double x = 1.5;

for (int i = 0; i < 6; i++) {
    x = x - (x * x - 3.0) / (2.0 * x);
}

printf("%.10f\\n", x);`
    })
  }),
  exercise({
    title: 'printf の戻り値を確認する',
    description: '`printf` の戻り値を変数へ保存し、何文字出力されたか確認できるようにしてください。',
    category: '入出力',
    difficulty: '初級',
    starterCode: program({
      mainBody: `int written;

// "C language" を出力し、そのときの printf の戻り値を written に保存して表示してください`
    }),
    solution: program({
      mainBody: `int written;

written = printf("C language\\n");
printf("written = %d\\n", written);`
    })
  }),
  exercise({
    title: 'scanf の戻り値を確認する',
    description: '整数を 1 つ読み取り、`scanf` が何項目読めたかと入力値を表示してください。',
    category: '入出力',
    difficulty: '初級',
    starterCode: program({
      mainBody: `int value;
int read_count;

// 整数を 1 つ読み取り、scanf の戻り値と value を表示してください`
    }),
    solution: program({
      mainBody: `int value;
int read_count;

read_count = scanf("%d", &value);
printf("read = %d\\n", read_count);
if (read_count == 1) {
    printf("value = %d\\n", value);
}`
    })
  }),
  exercise({
    title: 'グラフィックスで的を描く',
    description: '同心円を描いて的のような図形を作成してください。',
    category: 'グラフィックス',
    difficulty: '中級',
    type: 'graphics',
    starterCode: program({
      includes: ['#include "graphics.h"'],
      mainBody: `// 同心円を描いてください`
    }),
    solution: program({
      includes: ['#include "graphics.h"'],
      mainBody: `clear_canvas();

set_color(255, 255, 255);
draw_circle(200, 200, 120);
set_color(255, 0, 0);
draw_circle(200, 200, 90);
set_color(255, 255, 255);
draw_circle(200, 200, 60);
set_color(255, 0, 0);
draw_circle(200, 200, 30);
update_frame();`
    })
  })
];

const guidedAdvancedExercisePlans = {
  '3つの入力値の平均': {
    starterCodeGuide: 'この starterCode には変数宣言だけが入っています。まず 3 つの整数を受け取る入力処理を書き、そのあと合計と平均の計算、最後に小数表示まで順に完成させてください。整数同士の割り算だと小数部分が落ちるので、平均の式では実数除算になる形を意識してください。',
    todoList: [
      '3 つの整数を 1 回の `scanf` で読み込んでください。',
      '3 つの値の合計を使って平均を計算し、整数除算にならない形へ直してください。',
      '`printf` で小数として平均を表示し、入力を変えても正しい値になるか確認してください。'
    ]
  },
  'うるう年判定': {
    starterCodeGuide: 'この starterCode は、年を 1 つ読み取って判定文を自分で組み立てる前提です。4 年ごと、100 年ごと、400 年ごとの条件が絡むので、条件式を 1 行で済ませるより、まず判定の意味を整理してから書くと崩れにくくなります。',
    todoList: [
      '西暦を 1 つ `scanf` で読み込んでください。',
      '400 で割り切れる年、4 で割り切れて 100 では割り切れない年をうるう年として判定してください。',
      '結果に応じて `leap` または `common` を表示してください。'
    ]
  },
  '九九表の作成': {
    starterCodeGuide: 'starterCode は本体が空なので、行と列の 2 重ループを自分で組み立てます。外側が段、内側が掛ける数になるようにすると、表として整った出力を作りやすくなります。',
    todoList: [
      '1 から 9 まで回る外側ループと内側ループを用意してください。',
      '各マスで `row * col` を計算して表示してください。',
      '1 行ごとに改行し、9×9 の表として読める形にしてください。'
    ]
  },
  '素数判定関数': {
    starterCodeGuide: 'この starterCode では、未実装の関数本体を埋める必要があります。`main` は呼び出し確認だけに使い、判定ロジックは `is_prime` の中へ閉じ込める形で完成させてください。2 未満の扱いと、どこまで割れば十分かがポイントです。',
    todoList: [
      '`is_prime` 関数内で、2 未満を素数ではないと返してください。',
      '2 から `i * i <= n` の範囲で割り切れる数があるか調べてください。',
      '割り切れたら 0、最後まで見つからなければ 1 を返す関数にしてください。'
    ]
  },
  '配列の右回転': {
    starterCodeGuide: 'starterCode には配列だけが用意されています。最後の要素を退避し、後ろから順にずらして先頭へ戻すと安全に 1 つ右回転できます。',
    todoList: [
      '配列の最後の要素を一時変数へ保存してください。',
      '後ろから前へ向かうループで、各要素を 1 つ右へずらしてください。',
      '退避していた値を先頭へ入れ、回転後の全要素を表示してください。'
    ]
  },
  '行列の対角和': {
    starterCodeGuide: 'この starterCode は 3×3 行列をすでに持っています。主対角は `matrix[i][i]` の形で読めるので、添字を 1 本だけ動かして合計すれば十分です。',
    todoList: [
      '合計を入れる変数を 0 で初期化してください。',
      '`matrix[i][i]` を使って主対角要素だけを加算してください。',
      '最後に合計を表示し、3×3 の対角だけを足せていることを確認してください。'
    ]
  },
  '文字列の逆順表示': {
    starterCodeGuide: 'starterCode には文字列だけがあるので、まず長さを把握してから末尾側へ向かって表示します。終端文字の 1 つ手前から 0 番目まで進めば、逆順表示になります。',
    todoList: [
      '終端文字 `\\0` に当たるまで進み、文字列長を数えてください。',
      '長さから 1 引いた位置を開始点にして、逆向きのループを書いてください。',
      '各文字を順に表示し、最後に改行を入れて結果を確認してください。'
    ]
  },
  'ポインタで最大値を返す': {
    starterCodeGuide: 'この starterCode は、戻り値ではなく出力引数で結果を返す練習です。`result` が指す先へ最大値を書き込む必要があるので、比較のたびに `*result` を更新する形で実装してください。',
    todoList: [
      '配列の先頭要素を初期最大値として `*result` に入れてください。',
      '残りの要素を順に比較し、より大きい値があれば `*result` を更新してください。',
      '`main` からアドレスを渡して呼び出し、最大値が表示されることを確認してください。'
    ]
  },
  '動的配列の平均': {
    starterCodeGuide: 'starterCode では確保だけが済んでいるので、まず `NULL` を確認し、そのあと配列へ値を入れて平均を求めます。動的メモリの演習なので、最後の `free` まで必ず書いてください。',
    todoList: [
      '`malloc` の戻り値を確認し、失敗時はその場で終了してください。',
      '確保した配列へ値を格納し、ループで合計を求めてください。',
      '平均を実数として表示し、最後に `free` で解放してください。'
    ]
  },
  'ビットフラグで権限判定': {
    starterCodeGuide: 'starterCode はフラグ変数だけを持っています。読み取り・書き込み・実行をそれぞれ別ビットで表す定数を決めて、OR で設定し、AND で判定する流れにしてください。',
    todoList: [
      '読み取り・書き込み・実行を表すビット定数を定義してください。',
      '読み取りと実行のフラグだけを立ててください。',
      '各権限が有効かどうかを AND で判定し、結果を表示してください。'
    ]
  },
  'popcountの実装': {
    starterCodeGuide: 'この starterCode では、値を右へずらしながら下位ビットを数える方法が使いやすいです。`value & 1u` で最下位ビットを見て、毎回 `value >>= 1` で次へ進めてください。',
    todoList: [
      '1 の個数を数える変数を 0 で初期化してください。',
      '値が 0 になるまでループし、下位ビットが 1 ならカウントを増やしてください。',
      '毎回右シフトして次のビットへ進み、最終的な個数を表示してください。'
    ]
  },
  '単方向リストへの先頭挿入': {
    starterCodeGuide: 'starterCode では先頭ポインタだけがあるので、新しいノードを都度作り、`node->next = head` の形で先頭へつなぎ替えていきます。確保失敗時の処理も忘れずに入れてください。',
    todoList: [
      'ノードを動的確保し、値と `next` を設定してください。',
      '新しいノードの `next` を現在の `head` にし、そのあと `head` を新ノードへ更新してください。',
      '先頭から走査して値を表示し、先頭挿入の順序になっていることを確認してください。'
    ]
  },
  'スタックのpushとpop': {
    starterCodeGuide: 'この starterCode は配列スタックの最小形です。`top` を「次に入れる位置」として扱うと、push は代入してから増やす、pop は減らしてから読む形にそろえられます。',
    todoList: [
      '`top` を使って 10、20、30 の順に push してください。',
      '1 回だけ pop し、最後に入れた値を取り出してください。',
      '取り出した値を表示し、LIFO になっていることを確認してください。'
    ]
  },
  'リングバッファの基本操作': {
    starterCodeGuide: 'starterCode には `head` と `tail` があるので、追加後と取り出し後に `% 4` で循環させる形へそろえると分かりやすいです。追加 2 回、取り出し 2 回の順で状態変化を追ってください。',
    todoList: [
      '`tail` 側へ値を 2 つ追加し、追加のたびに剰余で次位置へ進めてください。',
      '`head` 側から値を 2 回取り出し、取り出し後に剰余で次位置へ進めてください。',
      '表示結果が追加順になっていることを確認してください。'
    ]
  },
  '二分探索関数': {
    starterCodeGuide: '未実装なのは探索関数本体です。`low`、`high`、`mid` の 3 つを使い、比較結果に応じて探索範囲を狭めていく形で組み立ててください。',
    todoList: [
      '`low` と `high` を初期化し、`low <= high` の間ループしてください。',
      '`mid` を計算して、目標値と一致するか比較してください。',
      '小さければ右側、大きければ左側へ範囲を更新し、見つからなければ `-1` を返してください。'
    ]
  },
  '2配列のマージ': {
    starterCodeGuide: 'starterCode には 2 つの整列済み配列と出力先だけがあります。`i`、`j`、`k` の 3 つの添字を用意し、小さい方を順に `merged` へ入れる形で進めてください。',
    todoList: [
      '2 つの入力配列と出力配列を走査する添字をそれぞれ用意してください。',
      '両方に要素が残っている間は、小さい方を `merged` へ入れて添字を進めてください。',
      '残った要素も追加し、完成した配列を順に表示してください。'
    ]
  },
  'BFSで到達判定': {
    starterCodeGuide: 'この starterCode は BFS の典型形です。キューと訪問済み配列を使い、開始頂点から近い順にたどって最後に目的頂点が訪問済みかを確認してください。',
    todoList: [
      '開始頂点をキューへ入れ、訪問済みとして記録してください。',
      'キューから取り出した頂点の隣接頂点を調べ、未訪問なら追加してください。',
      '探索後に目的頂点が訪問済みかどうかを表示してください。'
    ]
  },
  '台形公式で積分': {
    starterCodeGuide: 'starterCode には関数定義だけがあるので、区間、分割数、刻み幅、合計を自分で用意します。各小区間の左右端で関数値を求め、台形面積を足し合わせる形で進めてください。',
    todoList: [
      '積分区間 `a` と `b`、分割数 `n`、刻み幅 `h` を用意してください。',
      '各区間の左右端 `x1`、`x2` を使って台形面積を合計へ加算してください。',
      '近似積分値を表示し、分割数を増やすと値が安定するかも確認してください。'
    ]
  },
  'ニュートン法で平方根': {
    starterCodeGuide: 'starterCode では初期値と反復式を自分で組み立てます。今回の目的は `sqrt(3)` なので、`x = x - (x*x - 3) / (2*x)` を数回繰り返せば近づきます。',
    todoList: [
      '初期値を 0 以外の適当な実数で用意してください。',
      'ニュートン法の更新式をループの中へ書き、数回反復してください。',
      '最後の近似値を小数で表示し、`sqrt(3)` に近いか確認してください。'
    ]
  },
  'printf の戻り値を確認する': {
    starterCodeGuide: 'この starterCode では `written` だけが宣言されています。最初の `printf` をそのまま呼ぶのではなく、戻り値を代入し、そのあと 2 行目で文字数を表示する順番にしてください。',
    todoList: [
      '`printf("C language\\n")` の戻り値を `written` へ代入してください。',
      '`written` の値を `written = ...` の形で 2 行目へ表示してください。',
      '1 行目と 2 行目の表示順が入れ替わっていないか確認してください。'
    ]
  },
  'scanf の戻り値を確認する': {
    starterCodeGuide: 'この starterCode では、入力値そのものだけでなく `scanf` の戻り値も扱います。まず戻り値を `read_count` へ入れ、そのあと読めたときだけ `value` を表示する流れで組み立ててください。',
    todoList: [
      '`scanf("%d", &value)` の戻り値を `read_count` に代入してください。',
      '1 行目で `read_count` を表示してください。',
      '`read_count == 1` のときだけ 2 行目で `value` を表示してください。'
    ]
  },
  'グラフィックスで的を描く': {
    starterCodeGuide: 'この starterCode はグラフィックス API を使う問題です。最初にキャンバスを初期化し、色を切り替えながら中心の同じ円を半径違いで重ね、最後に `update_frame()` で反映してください。',
    todoList: [
      '`clear_canvas()` を呼んで描画を開始してください。',
      '中心座標をそろえた円を、色を変えながら複数描いてください。',
      '最後に `update_frame()` を呼び、的の形が画面に出るようにしてください。'
    ]
  }
};

const advancedExercises = rawAdvancedExercises.map((item) => {
  const guide = guidedAdvancedExercisePlans[item.title];
  if (!guide) {
    return item;
  }

  return {
    ...item,
    starterCodeGuide: guide.starterCodeGuide,
    todoList: guide.todoList
  };
});

const errorExercise = (config) =>
  exercise({
    format: 'エラー探し',
    ...config
  });

const errorExercises = [
  errorExercise({
    title: 'scanf のアドレス渡し忘れ',
    description: '整数入力を受け取るつもりのコードにあるバグを見つけて修正してください。',
    category: '入出力',
    difficulty: '初級',
    starterCode: program({
      mainBody: `int value = 0;

// バグを修正し、1つの整数を正しく読み取ってそのまま表示してください
scanf("%d", value);
printf("%d\\n", value);`
    }),
    solution: program({
      mainBody: `int value = 0;

scanf("%d", &value);
printf("%d\\n", value);`
    })
  }),
  errorExercise({
    title: 'if 文での代入ミス',
    description: '比較のつもりが代入になっている条件式を修正してください。',
    category: '条件分岐',
    difficulty: '初級',
    starterCode: program({
      mainBody: `int score = 75;

// score が 80 以上なら pass、そうでなければ fail と表示してください
if (score = 80) {
    printf("pass\\n");
} else {
    printf("fail\\n");
}`
    }),
    solution: program({
      mainBody: `int score = 75;

if (score >= 80) {
    printf("pass\\n");
} else {
    printf("fail\\n");
}`
    })
  }),
  errorExercise({
    title: '配列走査の off-by-one',
    description: '配列の範囲外を読んでしまうループを修正してください。',
    category: '配列',
    difficulty: '初級',
    starterCode: program({
      mainBody: `int values[5] = {1, 2, 3, 4, 5};
int total = 0;

// 配列の合計 15 を安全に計算して表示してください
for (int i = 0; i <= 5; i++) {
    total += values[i];
}
printf("%d\\n", total);`
    }),
    solution: program({
      mainBody: `int values[5] = {1, 2, 3, 4, 5};
int total = 0;

for (int i = 0; i < 5; i++) {
    total += values[i];
}
printf("%d\\n", total);`
    })
  }),
  errorExercise({
    title: '文字列比較で == を使っている',
    description: '文字列内容の比較方法を正しい書き方へ修正してください。',
    category: '文字列',
    difficulty: '初級',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <string.h>'],
      mainBody: `char command[] = "yes";

// command が "yes" のときだけ accepted と表示してください
if (command == "yes") {
    printf("accepted\\n");
}`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <string.h>'],
      mainBody: `char command[] = "yes";

if (strcmp(command, "yes") == 0) {
    printf("accepted\\n");
}`
    })
  }),
  errorExercise({
    title: '平均計算の整数除算',
    description: '平均値が切り捨てられてしまう見えにくい型の問題を修正してください。',
    category: '基礎',
    difficulty: '初級',
    starterCode: program({
      mainBody: `int a = 4;
int b = 5;
int c = 6;
double average = (a + b + c) / 3;

// 5.00 と表示されるように修正してください
printf("%.2f\\n", average);`
    }),
    solution: program({
      mainBody: `int a = 4;
int b = 5;
int c = 6;
double average = (a + b + c) / 3.0;

printf("%.2f\\n", average);`
    })
  }),
  errorExercise({
    title: 'switch の break 抜け',
    description: '分岐が意図せず次の case へ流れてしまうバグを修正してください。',
    category: '条件分岐',
    difficulty: '初級',
    starterCode: program({
      mainBody: `int level = 2;

// level が 2 のとき medium だけ表示されるように修正してください
switch (level) {
    case 1:
        printf("low\\n");
        break;
    case 2:
        printf("medium\\n");
    case 3:
        printf("high\\n");
        break;
    default:
        printf("unknown\\n");
}`
    }),
    solution: program({
      mainBody: `int level = 2;

switch (level) {
    case 1:
        printf("low\\n");
        break;
    case 2:
        printf("medium\\n");
        break;
    case 3:
        printf("high\\n");
        break;
    default:
        printf("unknown\\n");
}`
    })
  }),
  errorExercise({
    title: 'malloc の確保サイズ不足',
    description: '一見動きそうに見える確保サイズのバグを修正してください。',
    category: 'メモリ',
    difficulty: '中級',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      mainBody: `int *values = malloc(5);
int total = 0;

// 5 個の int を安全に格納し、合計を表示してください
if (values == NULL) {
    return 1;
}

for (int i = 0; i < 5; i++) {
    values[i] = (i + 1) * 10;
    total += values[i];
}

printf("%d\\n", total);
free(values);`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      mainBody: `int *values = malloc(5 * sizeof(int));
int total = 0;

if (values == NULL) {
    return 1;
}

for (int i = 0; i < 5; i++) {
    values[i] = (i + 1) * 10;
    total += values[i];
}

printf("%d\\n", total);
free(values);`
    })
  }),
  errorExercise({
    title: 'ローカル変数のアドレスを返している',
    description: '関数を抜けたあと寿命が切れる領域を返しているコードを修正してください。',
    category: 'ポインタ',
    difficulty: '中級',
    starterCode: program({
      helpers: `int *dangerous_ptr(void) {
    int value = 42;
    return &value;
}`,
      mainBody: `// 42 を安全に表示できるように修正してください
int *p = dangerous_ptr();
printf("%d\\n", *p);`
    }),
    solution: program({
      helpers: `int safe_value(void) {
    int value = 42;
    return value;
}`,
      mainBody: `int value = safe_value();
printf("%d\\n", value);`
    })
  }),
  errorExercise({
    title: 'NULL チェック前の逆参照',
    description: 'NULL を受け取ったときに落ちる関数を安全に修正してください。',
    category: 'ポインタ',
    difficulty: '中級',
    starterCode: program({
      helpers: `void print_first(int *ptr) {
    printf("%d\\n", *ptr);
    if (ptr == NULL) {
        printf("null\\n");
    }
}`,
      mainBody: `int *ptr = NULL;

// NULL でも安全に動くように修正してください
print_first(ptr);`
    }),
    solution: program({
      helpers: `void print_first(int *ptr) {
    if (ptr == NULL) {
        printf("null\\n");
        return;
    }
    printf("%d\\n", *ptr);
}`,
      mainBody: `int *ptr = NULL;
print_first(ptr);`
    })
  }),
  errorExercise({
    title: 'free 後の値参照',
    description: '解放済みメモリを再利用してしまうコードを修正してください。',
    category: 'メモリ',
    difficulty: '中級',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      mainBody: `int *value = malloc(sizeof(int));

// 25 を表示しつつ、解放後のメモリを読まないように修正してください
if (value == NULL) {
    return 1;
}

*value = 25;
free(value);
printf("%d\\n", *value);`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      mainBody: `int *value = malloc(sizeof(int));

if (value == NULL) {
    return 1;
}

*value = 25;
printf("%d\\n", *value);
free(value);
value = NULL;`
    })
  }),
  errorExercise({
    title: 'realloc 失敗で元ポインタを失う',
    description: '失敗時にメモリリークを起こす realloc の使い方を修正してください。',
    category: 'メモリ',
    difficulty: '上級',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      mainBody: `int *values = malloc(2 * sizeof(int));

// realloc が失敗しても元の領域を失わないように修正してください
if (values == NULL) {
    return 1;
}

values[0] = 10;
values[1] = 20;
values = realloc(values, 4 * sizeof(int));
if (values == NULL) {
    return 1;
}

values[2] = 30;
values[3] = 40;
printf("%d\\n", values[3]);
free(values);`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      mainBody: `int *values = malloc(2 * sizeof(int));

if (values == NULL) {
    return 1;
}

values[0] = 10;
values[1] = 20;

int *tmp = realloc(values, 4 * sizeof(int));
if (tmp == NULL) {
    free(values);
    return 1;
}
values = tmp;

values[2] = 30;
values[3] = 40;
printf("%d\\n", values[3]);
free(values);`
    })
  }),
  errorExercise({
    title: 'ビット判定の優先順位ミス',
    description: 'ビットマスク判定で括弧不足により条件が壊れているコードを修正してください。',
    category: 'ビット演算',
    difficulty: '中級',
    starterCode: program({
      helpers: `enum {
    READY_FLAG = 1 << 0
};`,
      mainBody: `unsigned int flags = READY_FLAG;

// READY_FLAG が立っているかどうかを正しく判定してください
if (flags & READY_FLAG == 0) {
    printf("not ready\\n");
} else {
    printf("ready\\n");
}`
    }),
    solution: program({
      helpers: `enum {
    READY_FLAG = 1 << 0
};`,
      mainBody: `unsigned int flags = READY_FLAG;

if ((flags & READY_FLAG) == 0) {
    printf("not ready\\n");
} else {
    printf("ready\\n");
}`
    })
  }),
  errorExercise({
    title: 'マクロの括弧不足',
    description: '式を引数に渡すと壊れるマクロ定義を修正してください。',
    category: '基礎',
    difficulty: '中級',
    starterCode: program({
      helpers: `#define SQUARE(x) x * x`,
      mainBody: `int value = 3;

// (value + 1) の二乗 16 が表示されるように修正してください
printf("%d\\n", SQUARE(value + 1));`
    }),
    solution: program({
      helpers: `#define SQUARE(x) ((x) * (x))`,
      mainBody: `int value = 3;
printf("%d\\n", SQUARE(value + 1));`
    })
  }),
  errorExercise({
    title: 'size_t の逆順ループ',
    description: '符号なし型の逆順ループで無限ループになるコードを修正してください。',
    category: 'ループ',
    difficulty: '上級',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <string.h>'],
      mainBody: `char text[] = "C-lang";
size_t length = strlen(text);

// 末尾から先頭へ安全に文字を表示してください
for (size_t i = length - 1; i >= 0; i--) {
    printf("%c", text[i]);
}
printf("\\n");`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <string.h>'],
      mainBody: `char text[] = "C-lang";
size_t length = strlen(text);

for (size_t i = length; i > 0; i--) {
    printf("%c", text[i - 1]);
}
printf("\\n");`
    })
  }),
  errorExercise({
    title: 'strncpy の終端文字不足',
    description: '見た目は安全そうでも文字列終端が抜けるコードを修正してください。',
    category: '文字列',
    difficulty: '上級',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <string.h>'],
      mainBody: `char src[] = "ABCDE";
char dst[5];

// dst に安全な文字列としてコピーして表示してください
strncpy(dst, src, sizeof(dst));
printf("%s\\n", dst);`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <string.h>'],
      mainBody: `char src[] = "ABCDE";
char dst[5];

strncpy(dst, src, sizeof(dst) - 1);
dst[sizeof(dst) - 1] = '\\0';
printf("%s\\n", dst);`
    })
  }),
  errorExercise({
    title: 'scanf %s の幅指定不足',
    description: '入力次第でバッファを壊す文字列入力を修正してください。',
    category: '文字列',
    difficulty: '中級',
    starterCode: program({
      mainBody: `char name[8];

// 長い入力でも配列外へ書き込まないように修正してください
scanf("%s", name);
printf("%s\\n", name);`
    }),
    solution: program({
      mainBody: `char name[8];

scanf("%7s", name);
printf("%s\\n", name);`
    })
  }),
  errorExercise({
    title: 'memset で int 配列を 1 初期化',
    description: 'バイト単位初期化と整数値初期化を取り違えているコードを修正してください。',
    category: '配列',
    difficulty: '上級',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <string.h>'],
      mainBody: `int values[4];

// 4 要素すべてを整数値 1 にして合計 4 を表示してください
memset(values, 1, sizeof(values));
printf("%d\\n", values[0] + values[1] + values[2] + values[3]);`
    }),
    solution: program({
      mainBody: `int values[4];

for (int i = 0; i < 4; i++) {
    values[i] = 1;
}
printf("%d\\n", values[0] + values[1] + values[2] + values[3]);`
    })
  }),
  errorExercise({
    title: 'qsort の比較関数が壊れている',
    description: '比較関数の戻り値と比較対象を正しく直してください。',
    category: 'アルゴリズム',
    difficulty: '上級',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      helpers: `int cmp_int(const void *lhs, const void *rhs) {
    const int *a = lhs;
    const int *b = rhs;
    return a > b;
}`,
      mainBody: `int values[5] = {7, 1, 4, 9, 2};

// 昇順に整列して表示してください
qsort(values, 5, sizeof(int), cmp_int);
for (int i = 0; i < 5; i++) {
    printf("%d\\n", values[i]);
}`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      helpers: `int cmp_int(const void *lhs, const void *rhs) {
    const int *a = lhs;
    const int *b = rhs;
    return (*a > *b) - (*a < *b);
}`,
      mainBody: `int values[5] = {7, 1, 4, 9, 2};

qsort(values, 5, sizeof(int), cmp_int);
for (int i = 0; i < 5; i++) {
    printf("%d\\n", values[i]);
}`
    })
  }),
  errorExercise({
    title: '二分探索の上端が 1 つ大きい',
    description: '一部の入力で配列外アクセスする探索関数を修正してください。',
    category: 'アルゴリズム',
    difficulty: '中級',
    starterCode: program({
      helpers: `int binary_search(int values[], int size, int target) {
    int low = 0;
    int high = size;

    while (low <= high) {
        int mid = (low + high) / 2;
        if (values[mid] == target) {
            return mid;
        }
        if (values[mid] < target) {
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }
    return -1;
}`,
      mainBody: `int values[5] = {2, 4, 6, 8, 10};

// 6 の添字 2 を安全に返すように修正してください
printf("%d\\n", binary_search(values, 5, 6));`
    }),
    solution: program({
      helpers: `int binary_search(int values[], int size, int target) {
    int low = 0;
    int high = size - 1;

    while (low <= high) {
        int mid = low + (high - low) / 2;
        if (values[mid] == target) {
            return mid;
        }
        if (values[mid] < target) {
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }
    return -1;
}`,
      mainBody: `int values[5] = {2, 4, 6, 8, 10};
printf("%d\\n", binary_search(values, 5, 6));`
    })
  }),
  errorExercise({
    title: '単方向リストの next 未初期化',
    description: 'リストの末尾が未定義のまま走査されるコードを修正してください。',
    category: 'データ構造',
    difficulty: '中級',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      helpers: `struct Node {
    int value;
    struct Node *next;
};`,
      mainBody: `struct Node *first = malloc(sizeof(struct Node));
struct Node *second = malloc(sizeof(struct Node));

// 10, 20 を順に表示できるようにリストを安全に組み立ててください
if (first == NULL || second == NULL) {
    free(first);
    free(second);
    return 1;
}

first->value = 10;
first->next = second;
second->value = 20;

for (struct Node *cur = first; cur != NULL; cur = cur->next) {
    printf("%d\\n", cur->value);
}

free(second);
free(first);`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      helpers: `struct Node {
    int value;
    struct Node *next;
};`,
      mainBody: `struct Node *first = malloc(sizeof(struct Node));
struct Node *second = malloc(sizeof(struct Node));

if (first == NULL || second == NULL) {
    free(first);
    free(second);
    return 1;
}

first->value = 10;
first->next = second;
second->value = 20;
second->next = NULL;

for (struct Node *cur = first; cur != NULL; cur = cur->next) {
    printf("%d\\n", cur->value);
}

free(second);
free(first);`
    })
  }),
  errorExercise({
    title: 'fopen の失敗を無視している',
    description: 'ファイルが開けない場合に落ちるコードを修正してください。',
    category: '入出力',
    difficulty: '中級',
    starterCode: program({
      mainBody: `FILE *fp = fopen("missing.txt", "r");
char buffer[32];

// ファイルが開けない場合も安全に終了できるよう修正してください
fgets(buffer, sizeof(buffer), fp);
printf("%s", buffer);
fclose(fp);`
    }),
    solution: program({
      mainBody: `FILE *fp = fopen("missing.txt", "r");
char buffer[32];

if (fp == NULL) {
    printf("open error\\n");
    return 1;
}

if (fgets(buffer, sizeof(buffer), fp) != NULL) {
    printf("%s", buffer);
}
fclose(fp);`
    })
  }),
  errorExercise({
    title: 'EOF を char で受けている',
    description: '入力終了判定が壊れる `getchar` の受け取り方を修正してください。',
    category: '入出力',
    difficulty: '上級',
    starterCode: program({
      mainBody: `char ch;
int count = 0;

// EOF まで安全に文字数を数えられるように修正してください
while ((ch = getchar()) != EOF) {
    count++;
}
printf("%d\\n", count);`
    }),
    solution: program({
      mainBody: `int ch;
int count = 0;

while ((ch = getchar()) != EOF) {
    count++;
}
printf("%d\\n", count);`
    })
  }),
  errorExercise({
    title: '符号付き右シフトの思い込み',
    description: '論理右シフトのつもりが算術右シフトになるコードを修正してください。',
    category: 'ビット演算',
    difficulty: '上級',
    starterCode: program({
      mainBody: `int value = 0xF0000000;
int shifted = value >> 4;

// 16 進数で 0F000000 と表示されるように修正してください
printf("%08X\\n", shifted);`
    }),
    solution: program({
      mainBody: `unsigned int value = 0xF0000000u;
unsigned int shifted = value >> 4;

printf("%08X\\n", shifted);`
    })
  }),
  errorExercise({
    title: '1 << 31 の未定義動作',
    description: '型の幅ぎりぎりを左シフトしている危険なコードを修正してください。',
    category: 'ビット演算',
    difficulty: '上級',
    starterCode: program({
      mainBody: `unsigned int mask = 1 << 31;

// 最上位ビットだけ立った値を安全に作って表示してください
printf("%08X\\n", mask);`
    }),
    solution: program({
      mainBody: `unsigned int mask = 1u << 31;
printf("%08X\\n", mask);`
    })
  }),
  errorExercise({
    title: '浮動小数点の直接比較',
    description: '見た目どおりに一致しない浮動小数点比較を修正してください。',
    category: '数値解析',
    difficulty: '中級',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <math.h>'],
      mainBody: `double total = 0.0;

// 0.1 を 10 回足した結果が 1.0 とみなせるように修正してください
for (int i = 0; i < 10; i++) {
    total += 0.1;
}

if (total == 1.0) {
    printf("equal\\n");
} else {
    printf("not equal\\n");
}`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <math.h>'],
      mainBody: `double total = 0.0;

for (int i = 0; i < 10; i++) {
    total += 0.1;
}

if (fabs(total - 1.0) < 1e-9) {
    printf("equal\\n");
} else {
    printf("not equal\\n");
}`
    })
  }),
  errorExercise({
    title: 'ニュートン法の 0 除算',
    description: '初期値によって 0 除算になる反復計算を修正してください。',
    category: '数値解析',
    difficulty: '上級',
    starterCode: program({
      mainBody: `double x = 0.0;

// sqrt(2) を安全に近似できるように修正してください
for (int i = 0; i < 6; i++) {
    x = x - (x * x - 2.0) / (2.0 * x);
}
printf("%.6f\\n", x);`
    }),
    solution: program({
      mainBody: `double x = 1.0;

for (int i = 0; i < 6; i++) {
    x = x - (x * x - 2.0) / (2.0 * x);
}
printf("%.6f\\n", x);`
    })
  }),
  errorExercise({
    title: '配列引数で sizeof を使っている',
    description: '関数引数へ渡った配列がポインタになる落とし穴を修正してください。',
    category: '配列',
    difficulty: '中級',
    starterCode: program({
      helpers: `int array_length(int values[]) {
    return sizeof(values) / sizeof(values[0]);
}`,
      mainBody: `int values[5] = {10, 20, 30, 40, 50};

// 要素数 5 が正しく表示されるように修正してください
printf("%d\\n", array_length(values));`
    }),
    solution: program({
      helpers: `int array_length(const int values[], int size) {
    return size;
}`,
      mainBody: `int values[5] = {10, 20, 30, 40, 50};
printf("%d\\n", array_length(values, 5));`
    })
  }),
  errorExercise({
    title: '式中の多重インクリメント',
    description: '未定義動作になるインクリメント式を予測可能な処理へ直してください。',
    category: '基礎',
    difficulty: '上級',
    starterCode: program({
      mainBody: `int i = 3;
int value = i++ + ++i;

// 処理順が明確なコードへ修正し、結果を表示してください
printf("%d %d\\n", i, value);`
    }),
    solution: program({
      mainBody: `int i = 3;
int left = i;
i++;
int right = i + 1;
i++;
int value = left + right;

printf("%d %d\\n", i, value);`
    })
  }),
  errorExercise({
    title: '文字列リテラルを書き換えている',
    description: '書き込み不可領域へ代入しているコードを修正してください。',
    category: '文字列',
    difficulty: '中級',
    starterCode: program({
      mainBody: `char *text = "hello";

// 先頭文字だけ大文字にして表示してください
text[0] = 'H';
printf("%s\\n", text);`
    }),
    solution: program({
      mainBody: `char text[] = "hello";

text[0] = 'H';
printf("%s\\n", text);`
    })
  }),
  errorExercise({
    title: 'ptrdiff_t の書式指定ミス',
    description: 'ポインタ差分の型に合わない書式指定子を修正してください。',
    category: 'ポインタ',
    difficulty: '上級',
    starterCode: program({
      includes: ['#include <stdio.h>', '#include <stddef.h>'],
      mainBody: `char text[] = "pointer";
ptrdiff_t diff = &text[6] - &text[1];

// ポインタ差分を正しい型と書式で表示してください
printf("%d\\n", diff);`
    }),
    solution: program({
      includes: ['#include <stdio.h>', '#include <stddef.h>'],
      mainBody: `char text[] = "pointer";
ptrdiff_t diff = &text[6] - &text[1];

printf("%td\\n", diff);`
    })
  })
];

export const exercises = [...lessonExercises, ...advancedExercises, ...errorExercises].map((item, index) => {
  const expectations = buildExerciseExpectations(item);

  return {
    id: index + 1,
    ...item,
    expectedBehavior: item.expectedBehavior || expectations.expectedBehavior,
    answerFormatNote: item.answerFormatNote || expectations.answerFormatNote,
    outputExample: item.outputExample || expectations.outputExample
  };
});
